import { FormField } from '@/global/components/Form'
import { RadioAccordion as RA } from '@/global/components/ui/Accordion'
import { cn } from '@/lib/utils'
import { Abono, RecorrenciaAbono } from '@/service/api'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

export const REPETICAO_DEFAULT_VALUES: RecorrenciaAbono = {
    tipoRepeticao: 'NAO_REPETE',
    every: 1,
    repeticaoMensalOpcao: 'DIA',
    dataFinal: undefined,
}

export default function RepeticaoAbonoAccordion() {
    const {
        watch,
        setValue,
        formState: { errors = {} },
    } = useFormContext<Abono & RecorrenciaAbono>()

    const { data: dataAbono, tipoRepeticao, every, dataFinal } = watch()
    const repeticaoSemanalDia = dayjs(dataAbono).day()

    const cantRepeat = !dataAbono || !dataFinal

    useEffect(() => {
        if (cantRepeat && tipoRepeticao !== 'NAO_REPETE')
            setValue('tipoRepeticao', 'NAO_REPETE')
    }, [cantRepeat])

    return (
        <RA.Root
            className="min-w-full"
            value={tipoRepeticao}
            onValueChange={v => {
                if (v) setValue('tipoRepeticao', v as any)
            }}
        >
            {errors.tipoRepeticao && (
                <p className="text-error text-xs">
                    {errors.tipoRepeticao.message}
                </p>
            )}
            <RA.Item value="NAO_REPETE">
                <RA.Trigger>Não repetir</RA.Trigger>
                <RA.Content className="flex flex-col gap-4 text-balance">
                    O abono será criado apenas uma vez,{' '}
                    {dataAbono
                        ? `em ${dayjs(dataAbono).format('DD/MM/YYYY')}`
                        : 'na data selecionada'}
                    .
                </RA.Content>
            </RA.Item>
            <RA.Item value="DIARIO" disabled={cantRepeat}>
                <RA.Trigger>
                    A cada&nbsp;
                    <FieldEvery />
                    &nbsp;dia(s)
                    {tipoRepeticao === 'DIARIO' && errors.every && (
                        <p className="text-error text-xs ml-2">
                            {errors.every.message}
                        </p>
                    )}
                    {cantRepeat && <AvisoCantRepeat />}
                </RA.Trigger>
                <RA.Content className="flex flex-col gap-4 text-balance">
                    <p className="text-xs w-full">
                        Os lançamentos acontecerão com {every} dia(s) de
                        intervalo entre eles.
                    </p>
                </RA.Content>
            </RA.Item>
            <RA.Item value="SEMANAL" disabled={cantRepeat}>
                <RA.Trigger>
                    A cada&nbsp;
                    <FieldEvery />
                    &nbsp;semana(s)
                    {tipoRepeticao === 'SEMANAL' && errors.every && (
                        <p className="text-error text-xs ml-2">
                            {errors.every.message}
                        </p>
                    )}
                    {cantRepeat && <AvisoCantRepeat />}
                </RA.Trigger>
                <RA.Content className="flex flex-col gap-4 text-balance">
                    <p className="text-xs w-full">
                        Os lançamentos acontecerão com um intervalo de {every}{' '}
                        semana(s) entre eles, sempre na(o){' '}
                        <strong>
                            {dayjs().day(repeticaoSemanalDia).format('dddd')}
                        </strong>
                        .
                    </p>
                </RA.Content>
            </RA.Item>
            <RA.Item value="MENSAL" disabled={cantRepeat}>
                <RA.Trigger>
                    A cada&nbsp;
                    <FieldEvery />
                    &nbsp;mes(es)
                    {tipoRepeticao === 'MENSAL' && errors.every && (
                        <p className="text-error text-xs ml-2">
                            {errors.every.message}
                        </p>
                    )}
                    {cantRepeat && <AvisoCantRepeat />}
                </RA.Trigger>
                <RA.Content className="flex flex-col gap-4 text-balance">
                    <RadioTipoMes />
                </RA.Content>
            </RA.Item>
        </RA.Root>
    )
}

/**
 * Correção para o overflow do RadioGroup dentro do Accordion
 */
function RadioTipoMes() {
    const { watch, setValue } = useFormContext<Abono & RecorrenciaAbono>()
    const dataAbono = watch('data')
    const repeticaoMensalOpcao = watch('repeticaoMensalOpcao')
    const { ordinal, diaSemana, semana, artigo } = calcularRepeticao(dataAbono)

    return [
        <div
            className={cn(
                'flex flex-col gap-2 w-full justify-evenly *:w-full',
                '*:cursor-pointer *:px-3 *:py-2 *:text-sm *:font-medium',
                '*:border-transparent *:rounded-full *:transition-all',
                '*:hover:bg-primary/10',
            )}
            key="form"
        >
            <div
                onClick={() => setValue('repeticaoMensalOpcao', 'DIA')}
                className={cn({
                    'ring-2 ring-primary text-primary':
                        repeticaoMensalOpcao === 'DIA',
                })}
            >
                Todo dia{' '}
                {dayjs
                    .utc(dataAbono || dayjs.utc().format('YYYY-MM-DD'))
                    .date()}{' '}
                do mês
            </div>
            <div
                onClick={() => setValue('repeticaoMensalOpcao', 'SEMANA')}
                className={cn({
                    'ring-2 ring-primary text-primary':
                        repeticaoMensalOpcao === 'SEMANA',
                })}
            >
                Tod{artigo} {semana}
                {ordinal} {diaSemana} do mês
            </div>
        </div>,
        <p className="text-xs w-full" key="explanation">
            {repeticaoMensalOpcao === 'DIA'
                ? 'Se lançado no dia 31 e o mês for fevereiro, com apenas 28 dias, ' +
                  'o próximo lançamento será feita no dia 3 de março. ' +
                  'Se o mês tiver apenas 30 dias e a troca for no dia 31, ' +
                  'a troca será feita no dia 1º do próximo mês.'
                : 'Se o lançamento for na 5ª semana e o mês não tiver ' +
                  'o dia específico na semana, o próximo ' +
                  'lançamento será feita na 1ª semana do mês seguinte.'}
        </p>,
    ]
}

function AvisoCantRepeat() {
    return [
        <div key="divider" className="flex flex-grow" />,
        <p
            className="text-xs text-balance text-muted-foreground bg-muted rounded-md p-2 shadow-sm"
            key="span"
        >
            Defina uma data de abono e uma data final.
        </p>,
    ]
}

function FieldEvery() {
    return (
        <FormField
            name="every"
            type="number"
            variant="filled"
            className="w-16 z-1 text-sm! text-center"
            onClick={e => e.stopPropagation()}
            min={0}
            max={999}
            placeholder="1"
            unHelperText
        />
    )
}

export function calcularRepeticao(data?: string | Date) {
    const dataDayjs = dayjs.utc(data || dayjs.utc().format('YYYY-MM-DD'))

    const indicadorOrdinal =
        dataDayjs.day() === 0 || dataDayjs.day() === 6 ? 'º' : 'ª'

    return {
        /* 'ª' ou 'º' */
        ordinal: indicadorOrdinal,
        /* Dia da semana em extenso */
        diaSemana: dataDayjs.format('dddd'),
        /* Calcula qual semana do mês é baseado no dia da data */
        semana: Math.floor((dataDayjs.date() - 1) / 7) + 1,
        /* 'o' ou 'a' */
        artigo: indicadorOrdinal === 'º' ? 'o' : 'a',
    }
}
